import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, Trash, Coins, Eye } from "@phosphor-icons/react";
import AdminUserSitesHoverCard from "@/components/AdminUserSitesHoverCard";

export default function AdminWebsites() {
  const [sites, setSites] = useState([]);
  const [tab, setTab] = useState("pending");

  const load = async () => {
    const { data } = await api.get(`/admin/websites?status=${tab}`);
    setSites(data);
  };
  useEffect(() => { load(); }, [tab]);

  const approve = async (s) => { await api.post(`/admin/websites/${s.id}/approve`); toast.success("Approved"); load(); };
  const reject = async (s) => {
    const reason = prompt("Rejection reason?");
    if (!reason) return;
    await api.post(`/admin/websites/${s.id}/reject`, { reason });
    toast.success("Rejected");
    load();
  };
  const remove = async (s) => {
    if (!window.confirm(`Permanently delete "${s.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/websites/${s.id}`);
      toast.success("Website deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Websites</h1>
        <div className="flex gap-1 rounded-full bg-slate-100 dark:bg-slate-800 p-1">
          {["pending", "approved", "rejected"].map((t) => (
            <button key={t} data-testid={`admin-sites-tab-${t}`} onClick={() => setTab(t)} className={`px-4 py-1.5 rounded-full text-sm font-bold capitalize transition-colors duration-200 ${tab === t ? "bg-blue-700 text-white" : "text-slate-600 dark:text-slate-300"}`}>{t}</button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {sites.length === 0 && <div className="rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 p-8 text-center text-slate-500">No {tab} websites.</div>}
        {sites.map((s) => (
          <div key={s.id} data-testid={`admin-site-${s.id}`} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[240px]">
              <div className="font-bold text-slate-900 dark:text-white">{s.title}</div>
              <a href={s.url} target="_blank" rel="noreferrer" className="text-xs text-blue-700 dark:text-blue-400 hover:underline break-all">{s.url}</a>
              <div className="text-xs text-slate-500 uppercase tracking-widest mt-1">{s.category}</div>
              {s.owner_username && (
                <div
                  data-testid={`admin-site-owner-${s.id}`}
                  className="text-xs text-slate-600 dark:text-slate-400 mt-1"
                  title={s.owner_email || ""}
                >
                  Submitted by{" "}
                  <AdminUserSitesHoverCard user={{ id: s.user_id, username: s.owner_username }}>
                    <span
                      className="font-bold text-slate-900 dark:text-white cursor-help hover:underline"
                      data-testid={`admin-site-owner-name-${s.id}`}
                    >
                      @{s.owner_username}
                    </span>
                  </AdminUserSitesHoverCard>
                  {s.owner_email ? (
                    <span className="text-slate-400"> · {s.owner_email}</span>
                  ) : null}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 text-sm shrink-0" data-testid={`admin-site-stats-${s.id}`}>
              <div className="text-center min-w-[64px]" title="Credits currently assigned by the owner">
                <div className="text-xs text-slate-500 uppercase tracking-widest flex items-center justify-center gap-1"><Coins size={12} weight="bold" /> Credits</div>
                <div className="font-black text-slate-900 dark:text-white">{Number(s.credits_assigned || 0).toFixed(0)}</div>
              </div>
              <div className="text-center min-w-[64px]" title="Total visits this site has received">
                <div className="text-xs text-slate-500 uppercase tracking-widest flex items-center justify-center gap-1"><Eye size={12} weight="bold" /> Visits</div>
                <div className="font-black text-slate-900 dark:text-white">{Number(s.views_earned || 0).toFixed(0)}</div>
              </div>
            </div>
            {tab === "pending" && (
              <div className="flex gap-2">
                <button data-testid={`admin-approve-${s.id}`} onClick={() => approve(s)} className="rounded-full bg-green-600 hover:bg-green-700 text-white font-bold px-3 py-1.5 text-sm inline-flex items-center gap-1 transition-colors duration-200"><CheckCircle size={14} weight="fill" /> Approve</button>
                <button data-testid={`admin-reject-${s.id}`} onClick={() => reject(s)} className="rounded-full bg-red-500 hover:bg-red-600 text-white font-bold px-3 py-1.5 text-sm inline-flex items-center gap-1 transition-colors duration-200"><XCircle size={14} weight="fill" /> Reject</button>
              </div>
            )}
            {tab !== "pending" && <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{s.status}</div>}
            <button
              data-testid={`admin-delete-site-${s.id}`}
              onClick={() => remove(s)}
              title="Delete website permanently"
              className="h-9 w-9 rounded-full bg-red-100 hover:bg-red-500 hover:text-white text-red-600 inline-flex items-center justify-center transition-colors duration-200"
            >
              <Trash size={16} weight="bold" />
            </button>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
